import { useState, useCallback, useEffect } from 'react';
import { INITIAL_SEARCH_PARAMS, INITIAL_ALERT_INFO, getBasicColumns, getPrintColumns } from '../constants/invoiceManagementConstants';

export const useInvoiceManagementState = (t, cachedState) => {
    const [searchParams, setSearchParams] = useState(() => cachedState?.searchParams ?? INITIAL_SEARCH_PARAMS);
    const [page, setPage] = useState(() => cachedState?.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showExtendedView, setShowExtendedView] = useState(false);
    const [showMyOrderOnly, setShowMyOrderOnly] = useState(false);
    const [checkedColumns, setCheckedColumns] = useState({});
    const [columnPanelOpen, setColumnPanelOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [alertInfo, setAlertInfo] = useState(INITIAL_ALERT_INFO);
    const [hasSearched, setHasSearched] = useState(false);
    const [invoiceData, setInvoiceData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);

    useEffect(() => {
        const initialState = {};
        getPrintColumns(t).forEach(column => {
            if (column.id === 'manager_name' || column.id === 'factory' || column.id === 'vat') {
                initialState[column.id] = false;
            } else {
                initialState[column.id] = true;
            }
        });
        setCheckedColumns(initialState);
    }, [t]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnPanelOpen) {
                const panel = document.getElementById('column-selection-panel');
                const settingsButton = document.getElementById('settings-button');
                if (
                    panel &&
                    !panel.contains(event.target) &&
                    settingsButton &&
                    !settingsButton.contains(event.target)
                ) {
                    setColumnPanelOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [columnPanelOpen]);

    const handleParamChange = useCallback((param, value) => {
        setSearchParams(prev => ({
            ...prev,
            [param]: value
        }));
    }, []);

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const handleCheckColumn = useCallback((columnId) => {
        setCheckedColumns(prev => ({
            ...prev,
            [columnId]: !prev[columnId]
        }));
    }, []);

    const toggleColumnPanel = useCallback(() => {
        setColumnPanelOpen(prev => !prev);
    }, []);

    const selectAllColumns = useCallback(() => {
        const newState = {};
        getPrintColumns(t).forEach(column => {
            newState[column.id] = true;
        });
        setCheckedColumns(newState);
    }, [t]);

    const unselectAllColumns = useCallback(() => {
        const newState = {};
        getPrintColumns(t).forEach(column => {
            newState[column.id] = false;
        });
        setCheckedColumns(newState);
    }, [t]);

    const handleSelectItem = useCallback((itemId) => {
        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    }, []);

    const handleSelectAll = useCallback((isSelected, currentPageIds = null) => {
        if (isSelected) {
            if (currentPageIds) {
                setSelectedItems(prev => {
                    const newSelected = [...prev];
                    currentPageIds.forEach(id => {
                        if (!newSelected.includes(id)) {
                            newSelected.push(id);
                        }
                    });
                    return newSelected;
                });
            } else {
                setSelectedItems(invoiceData.map(item => item.invoice_id));
            }
        } else {
            if (currentPageIds) {
                setSelectedItems(prev => prev.filter(id => !currentPageIds.includes(id)));
            } else {
                setSelectedItems([]);
            }
        }
    }, [invoiceData]);

    const handleCloseAlert = useCallback(() => {
        setAlertInfo(prev => ({
            ...prev,
            open: false
        }));
    }, []);

    return {
        searchParams,
        page,
        rowsPerPage,
        showExtendedView,
        showMyOrderOnly,
        checkedColumns,
        columnPanelOpen,
        selectedItems,
        hasSearched,
        invoiceData,
        totalCount,
        alertInfo,
        statusUpdateDialogOpen,
        setAlertInfo,
        setStatusUpdateDialogOpen,
        setSelectedItems,
        setSearchParams,
        setPage,
        setInvoiceData,
        setTotalCount,
        setHasSearched,
        setShowExtendedView,
        setShowMyOrderOnly,
        handleParamChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleCheckColumn,
        toggleColumnPanel,
        selectAllColumns,
        unselectAllColumns,
        handleSelectItem,
        handleSelectAll,
        handleCloseAlert
    };
}; 